import * as Sentry from "@sentry/react";
import { jwtDecode } from "jwt-decode";
import type { Role } from "@/types/common";
import type { UserToken } from "@/types/data";
import type { Heap } from "@/types/view";
import fetchWrapper from "./fetchWrapper";
import logger from "./logger";
import { deleteJwtToken, getJwtToken } from "./storage.helpers";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    heap?: Heap;
    _globalTimeInterval?: ReturnType<typeof setTimeout>;
  }
}

export const getUserProperties = (token = getJwtToken()) => {
  if (!token) return {};

  const user = jwtDecode<UserToken>(token);
  return {
    userId: user.userId,
    userDomain: user.sub.split("@").pop(),
    customerID: user.customerId,
    roles: user.roles,
  } as const;
};

/** Identify user and track non-PII in Heap */
export const identifyAndTrackUser = (token = getJwtToken()) => {
  if (!token) {
    logger.log("No user identified from session");
    return;
  }

  const { userId, ...user } = getUserProperties(token);
  const userProperties = { ...user, role: user.roles?.[0] };

  Sentry.setTags({
    roles: user.roles?.join(","),
    userDomain: user.userDomain,
    customerID: user.customerID,
  });

  if (userId && window.heap?.addUserProperties) {
    window.heap.identify(userId);
    window.heap.addUserProperties(userProperties);
  }
};

export const isInIframe = () => window?.top !== window;

/**
 * Async method to logout the user and redirect to login
 */
export const logoutTBUserSession = async () => {
  return fetchWrapper("/api/auth/logout", {
    method: "POST",
    ignoreResponse: true,
    useXAuthHeader: true,
  })
    .catch((err: unknown) => {
      logger.log(err);
    })
    .finally(() => {
      deleteJwtToken();
    });
};

export const redirectUri = () =>
  `${window.location.origin}${import.meta.env.VITE_ROUTER_BASE}`;

export const logoutUri = () => `${window.location.origin}/login`;

/**
 * Checks user roles against a given list.
 */
export const userHasRole = (userRoles: Role[], allowedRoles: Role[] = []) => {
  return allowedRoles.some((allowedRole) => userRoles.includes(allowedRole));
};
